import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getData as getInterviews } from "../../models/InterviewsModel";
import { getData as getOnboards } from "../../models/OnboardsModel";
import { checkPermission } from "../../helpers/GeneralHelper";
import {
  buildOnboardPrefillFromCandidate,
  resolveCandidateNextAction,
} from "../../helpers/CandidateNextActionHelper";

export default function CandidateNextActionBanner({
  candidate,
  statusOptions = [],
  module,
  onScheduleInterview,
}) {
  const { state } = useAuth();
  const userToken = state.user_data?.user_token;
  const [counts, setCounts] = useState({
    interviews: 0,
    onboards: 0,
    loading: true,
  });

  useEffect(() => {
    if (!userToken || !candidate?._candidate_id) {
      setCounts({ interviews: 0, onboards: 0, loading: false });
      return;
    }

    let cancelled = false;

    const loadCounts = async () => {
      setCounts((prev) => ({ ...prev, loading: true }));
      try {
        const candidateId = candidate._candidate_id;
        const [interviewsRes, onboardsRes] = await Promise.all([
          getInterviews({
            user_token: userToken,
            page: 1,
            limit: 1,
            interview_candidate_id: candidateId,
          }),
          getOnboards({
            user_token: userToken,
            page: 1,
            limit: 1,
            onboard_candidate_id: candidateId,
          }),
        ]);

        if (cancelled) return;

        setCounts({
          interviews: interviewsRes?.pagination?.total_rows ?? 0,
          onboards: onboardsRes?.pagination?.total_rows ?? 0,
          loading: false,
        });
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setCounts((prev) => ({ ...prev, loading: false }));
        }
      }
    };

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [userToken, candidate?._candidate_id]);

  const action = useMemo(
    () =>
      resolveCandidateNextAction({
        candidate,
        statusOptions,
        interviewCount: counts.interviews,
        onboardCount: counts.onboards,
      }),
    [candidate, statusOptions, counts.interviews, counts.onboards]
  );

  if (!action) return null;

  const candidateId = candidate?._candidate_id;
  const interviewsLink = `/interviews/?_candidate_id=${candidateId}`;
  const onboardsLink = `/onboards/?_candidate_id=${candidateId}`;
  const onboardCreateLink = {
    pathname: "/onboards/create",
    state: {
      module: {
        title: "Onboards",
        singularTitle: "Onboard",
        key: "onboards",
        route: "onboards",
        primaryCol: "_onboard_id",
      },
      prefill: buildOnboardPrefillFromCandidate(candidate),
    },
  };
  const editLink = candidate?.candidate_unique_id
    ? `/candidates/${candidate.candidate_unique_id}/edit`
    : null;

  const canAddInterview = checkPermission("interviews", "add");
  const canAddOnboard = checkPermission("onboards", "add");
  const canEditCandidate = checkPermission("candidates", "edit");

  const renderActionButton = (actionDef, variant = "primary") => {
    if (!actionDef) return null;

    const { id, label, icon } = actionDef;

    if (id === "schedule_interview") {
      if (!canAddInterview || !onScheduleInterview) return null;
      return (
        <button
          type="button"
          className={`btn btn-sm btn-${variant}`}
          onClick={onScheduleInterview}
        >
          <i className={`${icon} me-1`} />
          {label}
        </button>
      );
    }

    if (id === "start_onboard") {
      if (!canAddOnboard) return null;
      return (
        <Link to={onboardCreateLink} className={`btn btn-sm btn-${variant}`}>
          <i className={`${icon} me-1`} />
          {label}
        </Link>
      );
    }

    if (id === "view_interviews") {
      return (
        <Link to={interviewsLink} className={`btn btn-sm btn-${variant === "primary" ? "outline-primary" : "light"}`}>
          <i className={`${icon} me-1`} />
          {label}
        </Link>
      );
    }

    if (id === "view_onboards") {
      return (
        <Link to={onboardsLink} className={`btn btn-sm btn-${variant === "primary" ? "outline-primary" : "light"}`}>
          <i className={`${icon} me-1`} />
          {label}
        </Link>
      );
    }

    if (id === "edit_candidate" && editLink && canEditCandidate && module) {
      return (
        <Link
          to={editLink}
          state={{ data: candidate, module }}
          className={`btn btn-sm btn-${variant}`}
        >
          <i className={`${icon} me-1`} />
          {label}
        </Link>
      );
    }

    return null;
  };

  const primaryButton = renderActionButton(action.primaryAction, "primary");
  const secondaryButton = renderActionButton(
    action.secondaryAction,
    action.primaryAction ? "light" : "primary"
  );

  const toneClass =
    action.tone === "success"
      ? "candidate-next-action-success"
      : action.tone === "warning"
      ? "candidate-next-action-warning"
      : action.tone === "secondary"
      ? "candidate-next-action-muted"
      : "candidate-next-action-primary";

  return (
    <div className={`candidate-next-action-banner ${toneClass} mb-3`}>
      <div className="d-flex align-items-start gap-3 flex-wrap">
        <div className={`candidate-next-action-icon candidate-next-action-icon-${action.tone === "info" ? "primary" : action.tone}`}>
          <i className={action.icon} />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
            <strong className="candidate-next-action-title">{action.title}</strong>
            {candidate?.status_name && (
              <span className="badge bg-light text-dark border">
                Status: {candidate.status_name}
              </span>
            )}
            {counts.loading && (
              <span className="text-muted small">Checking pipeline…</span>
            )}
          </div>
          <p className="candidate-next-action-message mb-0">{action.message}</p>
        </div>
        {(primaryButton || secondaryButton) && (
          <div className="d-flex flex-wrap gap-2 align-items-center ms-auto">
            {primaryButton}
            {secondaryButton}
          </div>
        )}
      </div>
    </div>
  );
}
